import { useEffect, useRef, useState } from "react";

import { ArrowLeft, ArrowRight, ChevronDown } from "lucide-react";
import { toast } from "utils/toast";

import { useCreateProperty } from "hooks/useCreateProperty";
import { useUpdateProperty } from "hooks/useUpdateProperty";

import { Property } from "js/components/types";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "js/components/ui/alert-dialog";
import { Badge } from "js/components/ui/badge";
import { Button } from "js/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "js/components/ui/dialog";
import { StepIndicator } from "js/components/ui/step-indicator";

import { LandlordSelectionStep } from "./LandlordSelectionStep";
import { PropertyFormStep } from "./PropertyFormStep";
import { PROPERTY_MODAL_STEPS } from "./constants";
import { usePropertyForm } from "./hooks/usePropertyForm";

interface CreatePropertyModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  propertyId?: string;
  setPropertyId: (id: string) => void;
  propertyData: Property[];
  setPropertyData: (data: Property[]) => void;
  setSelectedProperty?: (property: Property) => void;
}

const CreatePropertyModal = ({
  isOpen,
  setIsOpen,
  propertyId,
  setPropertyId,
  propertyData,
  setPropertyData,
  setSelectedProperty,
}: CreatePropertyModalProps) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const isEditMode = !!propertyId;

  const {
    formData,
    updateFormData,
    selectedLandlord,
    setSelectedLandlord,
    resetForm,
    validateStep1,
    hasFormBeenModified,
    getFieldError,
    setFieldTouched,
    touchedFields,
    validateAllFields,
  } = usePropertyForm(isEditMode);

  const createPropertyMutation = useCreateProperty();
  const updatePropertyMutation = useUpdateProperty();

  const isLoading =
    createPropertyMutation.isPending || updatePropertyMutation.isPending;

  const scrollToTop = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  };

  const scrollToBottom = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: scrollContainerRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  };

  const checkScrollable = () => {
    if (scrollContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } =
        scrollContainerRef.current;
      const hasScrollableContent = scrollHeight > clientHeight;
      const isNotAtBottom = scrollTop + clientHeight < scrollHeight - 5;
      setShowScrollIndicator(hasScrollableContent && isNotAtBottom);
    }
  };

  const handleNext = () => {
    validateAllFields();
    if (!validateStep1()) return;
    if (isEditMode) {
      handleSubmit();
    } else {
      setCurrentStep(2);
      setTimeout(scrollToTop, 50);
    }
  };

  const handleBack = () => {
    setCurrentStep(1);
    setTimeout(scrollToTop, 50);
  };

  const handleStepClick = (stepNumber: number) => {
    if (stepNumber === 1) {
      setCurrentStep(1);
    } else if (stepNumber === 2 && validateStep1() && !isEditMode) {
      setCurrentStep(2);
    }
  };

  const handleSubmit = () => {
    validateAllFields();
    if (!validateStep1()) {
      setCurrentStep(1);
      return;
    }

    const propertyPayload: any = {
      alias: formData.alias || null,
      rental_price: formData.rental_price || null,
      maintenance_price: formData.maintenance_price || "0",
      address: {
        street: formData.street || null,
        exterior_number: formData.exterior_number || null,
        interior_number: formData.interior_number || null,
        zip_code: formData.zip_code || null,
        neighborhood: formData.colonia || null,
        municipality: formData.municipality || null,
        city: formData.city || null,
        state: formData.state || null,
      },
    };

    if (propertyId) {
      propertyPayload.id = propertyId;

      if (formData.uso) {
        propertyPayload.usage = formData.uso;
      }

      updatePropertyMutation.mutate(
        { property: propertyPayload, propertyId },
        {
          onSuccess: (property) => {
            setPropertyData(
              propertyData.map((item) =>
                item.id === propertyId ? { ...item, ...property } : item,
              ),
            );
            toast.success(
              "Se ha actualizado correctamente la información del inmueble",
            );
            handleClose();
          },
          onError: () => {
            toast.error("Algo salió mal, inténtelo de nuevo.");
          },
        },
      );
    } else {
      propertyPayload.usage = formData.uso || null;

      if (
        selectedLandlord &&
        Object.keys(selectedLandlord).length > 0 &&
        (selectedLandlord as any).id
      ) {
        propertyPayload.landlord_id = (selectedLandlord as any).id;
      } else {
        propertyPayload.landlord_id = null;
      }

      createPropertyMutation.mutate(propertyPayload, {
        onSuccess: (propertyResponse) => {
          if (setSelectedProperty) {
            setSelectedProperty(propertyResponse);
          }
          setPropertyData([...propertyData, propertyResponse]);
          toast.success("Se ha creado correctamente el inmueble");
          handleClose();
        },
        onError: () => {
          toast.error("Algo salió mal, inténtelo de nuevo.");
        },
      });
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setPropertyId("");
    resetForm();
    setCurrentStep(1);
  };

  const handleCancelClick = () => {
    if (hasFormBeenModified()) {
      setShowCancelConfirmation(true);
    } else {
      handleClose();
    }
  };

  const handleConfirmCancel = () => {
    setShowCancelConfirmation(false);
    handleClose();
  };

  const handleDialogOpenChange = (open: boolean) => {
    if (!open) {
      handleCancelClick();
    } else {
      setIsOpen(open);
    }
  };

  useEffect(() => {
    if (isOpen && propertyId && propertyData.length > 0) {
      const data = propertyData.find((property) => property.id === propertyId);
      if (data) {
        updateFormData(
          {
            uso: data.usage || "",
            alias: data.alias || "",
            rental_price: String(data.rental_price || ""),
            maintenance_price: String(data.maintenance_price || ""),
            street: data.address?.street || "",
            exterior_number: data.address?.exterior_number || "",
            interior_number: data.address?.interior_number || "",
            zip_code: data.address?.zip_code || "",
            colonia: data.address?.neighborhood || "",
            municipality: data.address?.municipality || "",
            city: data.address?.city || "",
            state: data.address?.state || "",
          },
          true,
        );
        setSelectedLandlord((data as any).landlord ?? {}, true);
      }
    } else if (isOpen && !propertyId) {
      resetForm();
    }
  }, [isOpen, propertyId, propertyData]);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      const handleScroll = () => {
        checkScrollable();
      };

      scrollContainer.addEventListener("scroll", handleScroll);

      const observer = new MutationObserver(() => {
        checkScrollable();
      });

      observer.observe(scrollContainer, {
        childList: true,
        subtree: true,
        attributes: true,
        characterData: true,
      });

      setTimeout(checkScrollable, 100);

      const handleResize = () => checkScrollable();
      window.addEventListener("resize", handleResize);

      return () => {
        scrollContainer.removeEventListener("scroll", handleScroll);
        window.removeEventListener("resize", handleResize);
        observer.disconnect();
      };
    }
  }, [currentStep]);

  useEffect(() => {
    setTimeout(checkScrollable, 100);
    setTimeout(checkScrollable, 500);
  }, [currentStep, isOpen]);

  useEffect(() => {
    checkScrollable();
    setTimeout(checkScrollable, 100);
  }, [
    formData.uso,
    formData.rental_price,
    formData.street,
    formData.zip_code,
    formData.colonia,
    selectedLandlord,
  ]);

  const steps = PROPERTY_MODAL_STEPS.map((step) => ({
    ...step,
    completed: currentStep > step.number,
  })) as Array<{
    number: number;
    title: string;
    subtitle?: string;
    icon: any;
    completed: boolean;
  }>;

  const getStepProgress = () => {
    if (currentStep === 1) return 0;
    if (currentStep === 2) return 100;
    return 0;
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleDialogOpenChange}>
      <DialogContent
        className="w-full max-w-[calc(100vw-2rem)] sm:max-w-4xl sm:min-w-[56rem] max-h-[calc(100dvh-2rem)] sm:max-h-[90vh] overflow-hidden flex flex-col rounded-none sm:rounded-lg"
        onInteractOutside={(e) => e.preventDefault()}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader className="pb-4 shrink-0">
          <DialogTitle className="text-lg sm:text-xl font-semibold text-gray-900">
            {propertyId ? "Editar" : "Registrar"} Inmueble
          </DialogTitle>
          {!isEditMode && (
            <StepIndicator
              steps={steps}
              currentStep={currentStep}
              onStepClick={handleStepClick}
              getStepProgress={getStepProgress}
            />
          )}
        </DialogHeader>

        <div
          ref={scrollContainerRef}
          className="relative flex-1 min-h-0 overflow-y-auto overflow-x-hidden"
        >
          {showScrollIndicator && (
            <div className="fixed bottom-24 right-4 z-50">
              <button
                onClick={scrollToBottom}
                className="bg-gray-500/80 backdrop-blur text-white px-3 py-2 rounded-full shadow-lg flex items-center gap-1 hover:bg-gray-600/80 transition-colors"
              >
                <span className="text-xs font-medium">Ver más</span>
                <ChevronDown className="w-4 h-4" />
              </button>
            </div>
          )}

          <div className="min-h-[400px] pb-4">
            {currentStep === 1 && (
              <PropertyFormStep
                formData={formData}
                updateFormData={updateFormData}
                getFieldError={getFieldError}
                setFieldTouched={setFieldTouched}
                touchedFields={touchedFields}
                isEditMode={!!propertyId}
              />
            )}
            {currentStep === 2 && (
              <LandlordSelectionStep
                selectedLandlord={selectedLandlord}
                onSelectLandlord={setSelectedLandlord}
              />
            )}
          </div>
        </div>

        <DialogFooter className="flex-row items-center justify-between pt-4 border-t border-gray-200 shrink-0 gap-2 sm:justify-between">
          {currentStep === 1 || isEditMode ? (
            <Button
              variant="ghost"
              onClick={handleCancelClick}
              disabled={isLoading}
              className="h-11 px-3 sm:px-6 border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Cancelar
            </Button>
          ) : (
            <Button
              variant="ghost"
              onClick={handleBack}
              disabled={isLoading}
              className="h-11 px-3 sm:px-6 border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              <ArrowLeft className="w-4 h-4 mr-1 sm:mr-2" />
              Regresar
            </Button>
          )}
          <div className="flex items-center gap-2 sm:gap-4">
            {!isEditMode && (
              <Badge
                variant="secondary"
                className="px-2 py-1 bg-gray-100 text-gray-700 text-[11px] sm:px-2.5 sm:text-xs"
              >
                <span className="sm:hidden">{currentStep}/2</span>
                <span className="hidden sm:inline">
                  Paso {currentStep} de 2
                </span>
              </Badge>
            )}
            {currentStep === 1 ? (
              <Button
                onClick={handleNext}
                disabled={!validateStep1() || isLoading}
                className="bg-[#0f5ef7] hover:bg-[#0d52e0] text-white h-11 px-4 sm:px-6 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isEditMode ? (
                  isLoading ? (
                    "Guardando..."
                  ) : (
                    "Guardar Cambios"
                  )
                ) : (
                  <>
                    Siguiente
                    <ArrowRight className="w-4 h-4 ml-1 sm:ml-2" />
                  </>
                )}
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={isLoading}
                className="bg-[#0f5ef7] hover:bg-[#0d52e0] text-white h-11 px-4 sm:px-6 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? "Guardando..." : "Registrar Inmueble"}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>

      <AlertDialog
        open={showCancelConfirmation}
        onOpenChange={setShowCancelConfirmation}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              ¿Estás seguro que deseas cancelar?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Has ingresado información en el formulario. Si cancelas ahora,
              perderás todos los datos ingresados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continuar editando</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmCancel}>
              Sí, cancelar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
};

export default CreatePropertyModal;
